import * as React from "react";
import dayjs from "dayjs";
import { Pagination, Popover, Table } from "antd";
import { ColumnsType, TableRowSelection } from "antd/lib/table/interface";
import { ArrowDownload16Regular, Open16Regular } from "@fluentui/react-icons";
import { CSVLink } from "react-csv";
import { formatDialect, spark } from "sql-formatter";
import IconTextButton from "../../iconTextButton/IconTextButton";
import AddToPackageButton from "../../addToPackageButton/AddToPackageButton";
import { EntityType } from "../../../types/EntityType";
import { IAiTableData, IAiSearchViewProps, IAiTablePaginationState, IAiSearchViewTableCodeState, IAiTableSelectionState } from "./AiSearchTypes";
import { findPackageActionWorkIdColumnIndex, findUsageIdColumnIndex, getAiTableKey } from "./AiSearchHelpers";
import AiCharts from "./AiCharts";

interface IAiGridProps {
    tables: IAiTableData[];
    pageName?: string;
    dataSource?: string;
    addToPackages?: IAiSearchViewProps["addToPackages"];
    packageResultPageData?: IAiSearchViewProps["packageResultPageData"];
    getWorkDetails?: IAiSearchViewProps["getWorkDetails"];
    getProductDetails?: IAiSearchViewProps["getProductDetails"];
    getUsageDetails?: IAiSearchViewProps["getUsageDetails"];
    tablePagination: IAiTablePaginationState;
    tableCodeVisibility: IAiSearchViewTableCodeState;
    tableSelection: IAiTableSelectionState;
    onTablePaginationChange: (tableKey: string, page: number, pageSize: number) => void;
    onToggleTableCode: (tableKey: string) => void;
    onSelectionChange: (tableKey: string, selectedRowKeys: (string | number)[]) => void;
}


const AiGrid = ({
    tables,
    pageName,
    dataSource,
    addToPackages,
    packageResultPageData,
    getWorkDetails,
    getProductDetails,
    getUsageDetails,
    tablePagination,
    tableCodeVisibility,
    tableSelection,
    onTablePaginationChange,
    onToggleTableCode,
    onSelectionChange
}: IAiGridProps) => {
    const formatCellValue = (value: any, options?: { colName?: string, entityType?: string, forCsv?: boolean }) => {
        const { colName, entityType, forCsv } = options || {};

        if (typeof value === "string") {
            try {
                const parsed = JSON.parse(value);
                if (typeof parsed === "object" && parsed !== null) {
                    if (options?.forCsv) { return JSON.stringify(parsed); }
                    const prettyJson = JSON.stringify(parsed, null, 2);
                    return (
                        <Popover content={<pre className="aiJsonPopover">{prettyJson}</pre>} placement="topRight">
                            <pre className="aiJsonCell" aria-label="JSON cell content">{prettyJson}</pre>
                        </Popover>
                    );
                }
            } catch {
                // no-op
            }
        }

        const isDateColumn = typeof colName === "string" && /date|time|timestamp/i.test(colName);
        if (typeof value === "string" && isDateColumn && dayjs(value).isValid() && value.includes("T")) {
            return dayjs(value).format("DD/MM/YYYY HH:mm:ss");
        }

        if (typeof value === "string" && value.includes("**")) {
            return value.split("**").map((segment, i) =>
                i % 2 ? <strong key={`ai-bold-${i}`}>{segment}</strong> : segment
            );
        }

        const idColNames = ["Id", "Work ID", "Product ID", "Usage ID"];
        if (colName && idColNames.includes(colName) && value && !forCsv) {
            const handlers: Record<string, ((e: React.MouseEvent) => void) | undefined> = {
                works: getWorkDetails && Number(value)
                    ? (e) => { e.preventDefault(); getWorkDetails(dataSource || "Repertoire", Number(value)); }
                    : undefined,
                products: getProductDetails && Number(value)
                    ? (e) => { e.preventDefault(); getProductDetails(dataSource || "Repertoire", Number(value)); }
                    : undefined,
                usages: getUsageDetails
                    ? (e) => { e.preventDefault(); getUsageDetails(String(value)); }
                    : undefined
            };
            const onClick = handlers[entityType || ""];
            if (onClick) {
                return (<a href="#" onClick={onClick} aria-label={`Go to details for ${colName}: ${value}`} role="link"> {value} </a>);
            }
        }

        return value;
    };

    const getAiTableCsvData = (table: IAiTableData) => {
        const rows = Array.isArray(table?.dataArray) ? table.dataArray : [];
        const columnCount = rows.reduce((max, row) => Math.max(max, Array.isArray(row) ? row.length : 0), 0);
        const headers = (table?.columnNames && table.columnNames.length)
            ? table.columnNames.slice(0, columnCount)
            : Array.from({ length: columnCount }, (_, i) => `Col ${i + 1}`);
        const data = rows.map(row =>
            Array.from({ length: columnCount }, (_, i) => formatCellValue(Array.isArray(row) ? row[i] : "", { forCsv: true }))
        );
        return [headers, ...data];
    };

    const renderAiTable = (table: IAiTableData, index: number) => {
        const tableKey = getAiTableKey(table, index);
        const page = tablePagination?.[tableKey]?.page || 1;
        const pageSize = tablePagination?.[tableKey]?.pageSize || 10;
        const rows = Array.isArray(table?.dataArray) ? table.dataArray : [];
        const columnCount = Math.max(0, ...rows.filter(Array.isArray).map(row => row.length));

        if (columnCount === 0) {
            return (
                <div key={`ai-table-${index}`} className="aiTableWrapper">
                    <div className="aiBubbleContent">No tabular rows.</div>
                </div>
            );
        }

        let entityType: "works" | "products" | "usages" = "works";
        const propSource = (pageName || "").toLowerCase();
        if (["works", "products", "usages"].includes(propSource)) {
            entityType = propSource as any;
        }
        const workIdColIndex = findPackageActionWorkIdColumnIndex(table, pageName);
        const canAddToPackage = addToPackages && packageResultPageData && workIdColIndex >= 0;

        const columns: ColumnsType<Record<string, any>> = Array.from({ length: columnCount }, (_, colIndex) => ({
            title: (
                <span>
                    <span>{table?.columnNames?.[colIndex] || `Col ${colIndex + 1}`}</span>
                    <span className="visually-hidden" role="columnheader" aria-label={table?.columnNames?.[colIndex] || `Column ${colIndex + 1}`}></span>
                </span>
            ),
            dataIndex: `col${colIndex}`,
            key: `col${colIndex}`,
            ellipsis: true,
            render: (value: any) => formatCellValue(value, { colName: table?.columnNames?.[colIndex], entityType }),
            sorter: (a: Record<string, any>, b: Record<string, any>) => {
                const valA = a[`col${colIndex}`];
                const valB = b[`col${colIndex}`];

                if (valA == null) return -1;
                if (valB == null) return 1;

                const numA = Number(String(valA).replace(/,/g, ""));
                const numB = Number(String(valB).replace(/,/g, ""));

                if (!isNaN(numA) && !isNaN(numB)) {
                    return numA - numB;
                }

                return String(valA).toLowerCase().localeCompare(String(valB).toLowerCase());
            }
        }));

        if (canAddToPackage) {
            columns.push({
                title: "Action",
                key: "aiPackageAction",
                width: 80,
                render: (_: any, record: Record<string, any>) => {
                    const workId = Number(record[`col${workIdColIndex}`]);
                    return (
                        <AddToPackageButton
                            entityType={EntityType.Work}
                            entityId={workId}
                            addToPackages={addToPackages}
                            resultPageData={packageResultPageData}
                            wrapperClassName="aiAddToPackageBtnWrap"
                            disabled={!workId}
                        />
                    );
                }
            });
        }

        const mappedData = rows.map((row: any, rowIndex: number) => {
            const record: Record<string, any> = { key: rowIndex };
            for (let colIndex = 0; colIndex < columnCount; colIndex += 1) {
                const cellValue = Array.isArray(row) ? row[colIndex] : undefined;
                record[`col${colIndex}`] = cellValue;
            }
            return record;
        });

        const pagedData = mappedData.slice((page - 1) * pageSize, page * pageSize);
        const isCodeVisible = tableCodeVisibility[tableKey] || false;

        const showUsageMatchAction = findUsageIdColumnIndex(table, pageName) >= 0;
        const showRowSelection = canAddToPackage || showUsageMatchAction;
        const selectedRowKeys = tableSelection[tableKey] || [];
        const rowSelection: TableRowSelection<Record<string, any>> | undefined = showRowSelection
            ? {
                selectedRowKeys,
                preserveSelectedRowKeys: true,
                onChange: (keys, _, info) => {
                    if (info.type === "all") {
                        const currentPageIsSelected = pagedData.every(record => keys.includes(record.key));
                        onSelectionChange(tableKey, currentPageIsSelected
                            ? mappedData.map(record => record.key)
                            : []);
                        return;
                    }

                    onSelectionChange(tableKey, keys as (string | number)[]);
                }
            }
            : undefined;

        return (
            <div key={`ai-table-${index}`} className="aiTableWrapper aiTableWrapperNoPad">
                <div className="aiTableActions aiTableActionsSticky">
                    <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
                        <CSVLink
                            data={getAiTableCsvData(table)}
                            filename={`ai-table-${index + 1}.csv`}
                            className="aiTableExportBtn"
                            target="_blank"
                        >
                            <IconTextButton
                                icon={ArrowDownload16Regular}
                                tooltip="Export CSV"
                                text={"Export"}
                            />
                        </CSVLink>
                        {table.query && (
                            <button
                                type="button"
                                className="aiTableShowCodeBtn"
                                onClick={() => onToggleTableCode(tableKey)}
                                title={isCodeVisible ? "Hide Code" : "Show Code"}
                            >
                                <Open16Regular className="aiTableCodeIcon" aria-hidden="true" />
                                {isCodeVisible ? "Hide Code" : "Show Code"}
                            </button>
                        )}
                    </div>
                </div>
                {table.query && isCodeVisible && (
                    <div className="aiTableQueryBlock" style={{ marginTop: 12 }}>
                        <pre className="aiSqlCodeBlock">
                            {formatDialect(String(table.query), { dialect: spark })}
                        </pre>
                    </div>
                )}
                <div className="aiTableScroll">
                    <Table
                        size="small"
                        pagination={false}
                        columns={columns}
                        dataSource={pagedData}
                        rowSelection={rowSelection}
                        className="aiAntTableFullWidth"
                        scroll={{ x: "max-content" }}
                    />
                </div>
                <div className="aiTableActions aiTableActionsBottomSticky">
                    <Pagination
                        current={page}
                        pageSize={pageSize}
                        total={mappedData.length}
                        showSizeChanger
                        pageSizeOptions={[10, 20, 50, 100]}
                        onChange={(p, ps) => onTablePaginationChange(tableKey, p, ps)}
                        className="aiPaginationNoMargin"
                    />
                </div>
            </div>
        );
    };

    return (
        <div className="aiTableGrid">
            {tables.map(renderAiTable)}
            <AiCharts tables={tables} />
        </div>
    );
};

export default AiGrid;
